import {
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { EntityAutoCompleteComponent } from '@gfe/shared/components/row-edition-form/entity-auto-complete/entity-auto-complete.component';

import { processUidValue } from '@shared/components/table/table-cell/utils';

@Component({
  selector: 'app-uid-cell-editor',
  standalone: true,
  imports: [EntityAutoCompleteComponent, ReactiveFormsModule],
  templateUrl: './uid-cell-editor.component.html',
  styles: ``
})
export class UidCellEditorComponent {
  // Standard table cell editor inputs
  value = input.required<unknown>();
  disabled = input<boolean>(false);
  tableCellWidth = input<number>(0);
  tableCellHeight = input<number>(0);

  // UID-specific configuration
  entityType = input.required<string>();
  /** Owning entity and column, so the picker scopes its presets to this field. */
  ownerEntityType = input<string>('');
  columnId = input<string>('');

  // Standard table cell editor output
  valueChange = output<string | null>();

  // Internal FormControl for entity-auto-complete
  protected formControl = new FormControl<string | null>(null);

  // Internal signal for entity-auto-complete binding
  protected entityValue = signal<string | null>(null);

  // Process initial value
  protected processedValue = computed<string | null>(
    () => processUidValue(this.value()) ?? null
  );

  constructor() {
    // Sync input value → entityValue and formControl
    // This runs when the parent changes the value input
    effect(() => {
      const value = this.processedValue();
      untracked(() => {
        this.entityValue.set(value);
        this.formControl.setValue(value, { emitEvent: false });
      });
    });

    // Sync entityValue changes → output
    // This runs when entity-auto-complete changes entityValue via two-way binding
    effect(() => {
      const value = this.entityValue();
      // Only emit if it's different from the input value to avoid unnecessary emissions
      if (value !== untracked(() => this.processedValue())) {
        untracked(() => {
          this.valueChange.emit(value);
        });
      }
    });

    // Handle disabled state
    effect(() => {
      if (this.disabled()) {
        this.formControl.disable({ emitEvent: false });
      } else {
        this.formControl.enable({ emitEvent: false });
      }
    });

    // Sync FormControl changes → entityValue signal
    // This completes the chain: user interaction → ControlValueAccessor → FormControl → entityValue → valueChange.emit()
    this.formControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      // Only update entityValue if it's actually different to avoid circular updates
      if (value !== this.entityValue()) {
        this.entityValue.set(value);
      }
    });
  }
}
